/**
 * Pure series appearance resolvers.
 *
 * Rules for all helpers:
 * - Never mutate the input series objects.
 * - Return a new series object with the resolved color applied.
 * - Derive colors from ChartAppearance — do not read series.color as a source
 *   of truth (prevents the stale-color bug introduced by ??= assignment).
 */

import type { ChartAppearance } from '@/components/Chart/theme/chartAppearance';
import type { ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';
import { regularChartPalette } from '@/constants/constants';
import {
  balancedPalette,
  monoBluePalette,
  vibrantPalette
} from '@/features/Dashboard/form/AppForm/components/ColorPaletteSelector/const';

import type { Color } from '@/features/Dashboard/form/AppForm/components/ColorPaletteSelector/const';

const AREA_RANGE_COLOR = '#cdcdb5';
const AREA_RANGE_FILL_OPACITY = 0.2;
const AVERAGE_COLOR = '#F8F8F8';
const HIGH_INDEX_FALLBACK = '#F8F8F8';
const HIGH_INDEX_THRESHOLD = 5;

/**
 * Core palette-based color resolver shared by spot and fullscreen resolvers.
 *
 * Priority:
 * 1. If combinedIndex > threshold: fixed fallback color.
 * 2. series.custom.color (explicit user override from application data).
 * 3. Appearance palette color at combinedIndex (cycled with modulo).
 *
 * Never reads series.color — only series.custom.color (application data) and
 * the appearance palette are used as sources of truth. This prevents the
 * stale-color bug caused by `??=` mutation.
 */
const resolveSeriesColorFromAppearance = (
  series: ExtendedSeriesOptionsType,
  appearance: ChartAppearance
): string => {
  const { combinedIndex } = series.custom;
  if (combinedIndex > HIGH_INDEX_THRESHOLD) return HIGH_INDEX_FALLBACK;
  const { palette } = appearance;
  return series.custom.color ?? palette[combinedIndex % palette.length];
};

// ─── Spot ────────────────────────────────────────────────────────────────────

/**
 * Resolves spot/regular series color using the shared appearance palette resolver.
 * Returns a new series object — does not mutate the input.
 */
export const withSpotSeriesAppearance = (
  series: ExtendedSeriesOptionsType,
  appearance: ChartAppearance
): ExtendedSeriesOptionsType => ({
  ...series,
  color: resolveSeriesColorFromAppearance(series, appearance)
});

// ─── Fullscreen ───────────────────────────────────────────────────────────────

/**
 * Resolves fullscreen series color using the shared appearance palette resolver.
 *
 * Uses the same logic as spot but is called with a different appearance
 * (fullscreen-light or fullscreen-dark), which provides the correct palette for
 * the current mode. By always deriving color from the palette — never from
 * series.color — this fixes the stale-color bug where `series.color ??= …`
 * only assigned once and the wrong mode's color persisted after switching.
 *
 * Returns a new series object — does not mutate the input.
 */
export const withFullscreenSeriesAppearance = (
  series: ExtendedSeriesOptionsType,
  appearance: ChartAppearance
): ExtendedSeriesOptionsType => ({
  ...series,
  color: resolveSeriesColorFromAppearance(series, appearance)
});

// ─── Forward curve ────────────────────────────────────────────────────────────

/**
 * Available forward-curve palettes. Order determines priority.
 * series.color is read here as legitimate application data — the color
 * represents the user-selected palette entry, not a previous Highcharts
 * mutation. The palette-matching logic uses it to re-index within the same
 * palette so all series stay consistent when the chart re-renders.
 */
const FORWARD_CURVE_PALETTES: readonly Color[][] = [
  vibrantPalette,
  monoBluePalette,
  balancedPalette
] as const;

const findPaletteForColor = (color: string | undefined): readonly Color[] | null => {
  if (!color || typeof color !== 'string') return null;
  const normalizedColor = color.toUpperCase();
  return (
    FORWARD_CURVE_PALETTES.find(palette =>
      palette.some(entry => entry.hex.toUpperCase() === normalizedColor)
    ) ?? null
  );
};

const resolveForwardCurveColor = (
  currentColor: string | undefined,
  combinedIndex: number
): string => {
  const matched = findPaletteForColor(currentColor);
  if (matched && combinedIndex >= 0) {
    return matched[combinedIndex % matched.length].hex;
  }
  // Fallback: regularChartPalette with safe negative-index handling.
  const len = regularChartPalette.length;
  const idx = combinedIndex < 0 ? ((combinedIndex % len) + len) % len : combinedIndex;
  return regularChartPalette[idx % len];
};

/**
 * Resolves forward-curve series color.
 *
 * Reads series.color to find which user-selected palette the series belongs
 * to, then re-indexes within that palette using combinedIndex. Returns a new
 * series object — does not mutate the input.
 */
export const withForwardCurveSeriesAppearance = (
  series: ExtendedSeriesOptionsType,
  _appearance: ChartAppearance
): ExtendedSeriesOptionsType => {
  const { combinedIndex } = series.custom;
  const currentColor = typeof series.color === 'string' ? series.color : undefined;
  return { ...series, color: resolveForwardCurveColor(currentColor, combinedIndex) };
};

// ─── Seasonal ─────────────────────────────────────────────────────────────────

/**
 * Resolves seasonal series color.
 *
 * Special cases (applied in order):
 * 1. arearange → fixed muted color + fill opacity.
 * 2. field === 'average' → fixed light color.
 * 3. numeric field (a year) → year-distance palette rule using regularChartPalette.
 * 4. fallback → regularChartPalette at combinedIndex.
 *
 * Note: seasonal always uses regularChartPalette for the year-distance rule,
 * regardless of the appearance mode, because the year-distance calculation is
 * relative to the current calendar year and not tied to light/dark palettes.
 */
export const withSeasonalSeriesAppearance = (
  series: ExtendedSeriesOptionsType,
  _appearance: ChartAppearance
): ExtendedSeriesOptionsType => {
  const { combinedIndex, field } = series.custom;

  if (series.type === 'arearange') {
    return { ...series, color: AREA_RANGE_COLOR, fillOpacity: AREA_RANGE_FILL_OPACITY };
  }

  if (field === 'average') {
    return { ...series, color: AVERAGE_COLOR };
  }

  if (!Number.isNaN(Number(field))) {
    const yearToYearIndex = new Date().getFullYear() - Number(field);
    return {
      ...series,
      color: regularChartPalette[yearToYearIndex % regularChartPalette.length]
    };
  }

  return { ...series, color: regularChartPalette[combinedIndex % regularChartPalette.length] };
};
